/**
 * LT-16 Part 3a-ii — lifecycle state banner.
 *
 * Fetches the Prepared version (if any), computes state, renders
 * a persistent banner at the top of every tender tab. Actions:
 *   - Working: no banner rendered.
 *   - Prepared: "PREPARED · V2 — not yet sent · Mark as Sent · Void"
 *     Shows stale-since-generated warning when signature drifts.
 *   - Issued: "ISSUED · V2 sent 14/09/2026 · Revise"
 *
 * The Issued banner is also the "workspace is read-only" signal —
 * combined with the API-level lock on writes, it stops anyone from
 * silently nudging figures on a submitted tender.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, FileCheck, GitBranch, Lock, RotateCcw, Trash2,
} from "lucide-react";
import Send from "lucide-react/dist/esm/icons/send";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ukDate } from "../lib/format";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const cls = (...xs) => xs.filter(Boolean).join(" ");

export default function LifecycleStateBanner({ tender, onTenderUpdate }) {
  const [prepared, setPrepared] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [issuedVersion, setIssuedVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [reviseOpen, setReviseOpen] = useState(false);
  // LT-17 §7e — shown inside the Revise dialog when at least one
  // labour rate the tender uses has moved since the last sent
  // version. Fetched lazily on dialog open. Default is `false`
  // (keep V<n>'s rates), matching the user's spec: "One question,
  // at the only moment it is relevant, with the safe answer as the
  // default."
  const [staleSinceSent, setStaleSinceSent] = useState(null);
  const [repriceLabour, setRepriceLabour] = useState(false);

  const load = useCallback(async () => {
    try {
      const [prep, versions] = await Promise.all([
        api.get(`/tenders/${tender.id}/versions/prepared`),
        api.get(`/tenders/${tender.id}/versions`),
      ]);
      setPrepared(prep.data?.prepared || null);
      setIsStale(!!prep.data?.is_stale);
      const list = versions.data?.versions || [];
      // Highest version number (LT-16 §3c — whole numbers only).
      list.sort((a, b) => b.version_number - a.version_number);
      setIssuedVersion(list[0] || null);
    } catch {
      setPrepared(null);
      setIsStale(false);
    }
  }, [tender.id]);

  // LT-17 §7e — fetch staleness only when the Revise dialog opens.
  useEffect(() => {
    if (!reviseOpen) return;
    let dropped = false;
    api.get(`/tenders/${tender.id}/labour-rate-staleness`)
      .then((r) => {
        if (dropped) return;
        setStaleSinceSent(r.data?.stale_since_last_sent || []);
      })
      .catch(() => { if (!dropped) setStaleSinceSent([]); });
    return () => { dropped = true; };
  }, [reviseOpen, tender.id]);

  useEffect(() => { load(); }, [load, tender.updated_at, tender.status, tender.current_version]);

  const markSent = async () => {
    if (!prepared) return;
    setBusy(true);
    try {
      await api.post(`/tenders/${tender.id}/versions/${prepared.version_ref}/mark-sent`);
      toast.success(`Version ${prepared.version_ref} marked as sent`);
      // Reload tender so downstream components see status=submitted.
      const r = await api.get(`/tenders/${tender.id}`);
      onTenderUpdate?.(r.data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark sent");
    } finally { setBusy(false); }
  };

  const doVoid = async () => {
    if (!prepared) return;
    if (!voidReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenders/${tender.id}/versions/${prepared.version_ref}/void`, {
        void_reason: voidReason.trim(),
      });
      toast.success(`Version ${prepared.version_ref} voided`);
      setVoidOpen(false); setVoidReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    } finally { setBusy(false); }
  };

  const revise = async () => {
    setBusy(true);
    try {
      await api.post(`/tenders/${tender.id}/revise`, {
        // LT-17 §7e — pass the user's choice from the dialog. Default
        // `false` keeps the sent version's rates on the revised copy;
        // `true` walks every line and writes the current master
        // hourly_rate onto rows we can link back (labour_rate_id
        // set).
        reprice_labour: !!repriceLabour,
      });
      toast.success(
        repriceLabour
          ? "Tender re-opened and labour rates updated to current"
          : "Tender re-opened for editing"
      );
      const r = await api.get(`/tenders/${tender.id}`);
      onTenderUpdate?.(r.data);
      setReviseOpen(false);
      setRepriceLabour(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revise failed");
    } finally { setBusy(false); }
  };

  const isIssued = tender.status === "submitted";
  const showPrepared = !!prepared && !isIssued;

  if (!isIssued && !showPrepared) {
    // Working state — no banner.
    return null;
  }

  if (isIssued) {
    const sentDate = issuedVersion?.sent_at ? ukDate(issuedVersion.sent_at) : "—";
    return (
      <div
        className="mb-5 px-4 py-3 rounded-md border-2 flex items-center justify-between gap-3"
        style={{ background: "#EFF4EE", borderColor: "#2F4F3E" }}
        data-testid="lifecycle-banner-issued"
      >
        <div className="flex items-center gap-3">
          <Lock size={16} className="text-accent" />
          <div className="flex items-center gap-2">
            <span className="font-mono uppercase text-accent" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em" }}>
              Issued
            </span>
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
              Version <span className="font-mono">{issuedVersion?.version_ref || tender.current_version}</span>{" "}
              sent <span className="font-mono">{sentDate}</span>
            </span>
            <span className="text-ink-3" style={{ fontSize: 12 }}>
              — the tender is read-only. Click Revise to re-open for editing (the sent version stays untouched).
            </span>
          </div>
        </div>
        <button
          onClick={() => setReviseOpen(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-accent text-white hover:bg-accent-deep disabled:opacity-40"
          style={{ fontSize: 12.5, fontWeight: 500 }}
          data-testid="lifecycle-revise-btn"
        >
          <RotateCcw size={13} /> Revise
        </button>

        <AlertDialog open={reviseOpen} onOpenChange={setReviseOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revise this tender?</AlertDialogTitle>
              <AlertDialogDescription>
                Revising re-opens the tender for editing. The current sent version (<strong>{issuedVersion?.version_ref}</strong>)
                stays untouched in history. Your next Create Client Estimate will bump to a new whole-number version.
                <br /><br />
                Clicking Revise and then changing your mind costs nothing — nothing is issued until you re-run Create Client Estimate.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {/* LT-17 §7e — one-off reprice question, only shown when at
                least one labour rate has moved since the sent
                version. Default is Keep — the safe answer per user
                spec. */}
            {Array.isArray(staleSinceSent) && staleSinceSent.length > 0 && (
              <div
                className="rounded-md border px-4 py-3"
                style={{ background: "#FEF3E2", borderColor: "#F2DFB8", color: "#7A4C0A" }}
                data-testid="revise-reprice-prompt"
              >
                <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
                  {staleSinceSent.length} labour rate{staleSinceSent.length === 1 ? " has" : "s have"} changed
                  since <span className="font-mono">{issuedVersion?.version_ref}</span> was issued on{" "}
                  <span className="font-mono">{ukDate(issuedVersion?.sent_at)}</span>.
                </div>
                <div className="flex flex-col gap-1.5" role="radiogroup">
                  <label className="inline-flex items-center gap-2 cursor-pointer" style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="reprice"
                      checked={!repriceLabour}
                      onChange={() => setRepriceLabour(false)}
                      data-testid="revise-reprice-keep"
                    />
                    <span><strong>Keep {issuedVersion?.version_ref}'s rates</strong> — normal case; nothing is repriced.</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer" style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="reprice"
                      checked={repriceLabour}
                      onChange={() => setRepriceLabour(true)}
                      data-testid="revise-reprice-update"
                    />
                    <span>Reprice at current rates — for an estimate that's sat with the client for months.</span>
                  </label>
                </div>
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel data-testid="lifecycle-revise-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={revise} data-testid="lifecycle-revise-confirm">Revise</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Prepared
  return (
    <div
      className={cls(
        "mb-5 px-4 py-3 rounded-md border-2 flex items-center justify-between gap-3",
        isStale ? "bg-note-red/5" : "",
      )}
      style={{
        background: isStale ? "#FFF1F1" : "#FEF3E2",
        borderColor: isStale ? "#B45309" : "#B45309",
      }}
      data-testid="lifecycle-banner-prepared"
    >
      <div className="flex items-center gap-3">
        <GitBranch size={16} className="text-note-amber" />
        <div className="flex items-center gap-2">
          <span className="font-mono uppercase text-note-amber" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em" }}>
            Prepared
          </span>
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
            Version <span className="font-mono">{prepared.version_ref}</span> — not yet sent
          </span>
          {isStale && (
            <span className="inline-flex items-center gap-1 text-note-red" style={{ fontSize: 12 }}>
              <AlertTriangle size={12} /> Edits since generation — regenerate before sending
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={markSent}
          disabled={busy || isStale}
          className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-accent text-white hover:bg-accent-deep disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ fontSize: 12.5, fontWeight: 500 }}
          title={isStale ? "Regenerate the version first — the tender has changed since it was prepared" : "Mark this version as sent to the client"}
          data-testid="lifecycle-mark-sent-btn"
        >
          <Send size={12} /> Mark as sent
        </button>
        <button
          onClick={() => setVoidOpen(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
          style={{ fontSize: 12 }}
          data-testid="lifecycle-void-btn"
        >
          <Trash2 size={12} /> Void
        </button>
      </div>

      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void this prepared version?</AlertDialogTitle>
            <AlertDialogDescription>
              Voiding {prepared.version_ref} discards the prepared PDF. The version number stays free
              — a fresh Create Client Estimate will use {prepared.version_ref} again. The void is logged
              to the audit trail; the version list on the Versions tab won't include it.
              <br /><br />
              A reason is required.
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. Typo in address caught before sending"
                className="w-full min-h-[64px] rounded-md border border-line bg-surface px-2.5 py-1.5 text-ink outline-none mt-3"
                style={{ fontSize: 12 }}
                data-testid="lifecycle-void-reason"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lifecycle-void-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doVoid}
              disabled={!voidReason.trim()}
              data-testid="lifecycle-void-confirm"
            >
              Void version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
